/**
 * categoryThemes.js — one theme per canonical vendor `business_type`.
 *
 * Each theme is pure CSS/SVG — zero photos, zero external assets, zero
 * AI generation. This is the entire brief's requirement: fast, free,
 * predictable, brand-controlled.
 *
 * Theme keys are LOWER_SNAKE slugs derived from the `BUSINESS_TYPES`
 * strings in `/app/frontend/src/lib/businessTypes.js` — see `slugify()`
 * below. `getCategoryTheme(businessType)` handles legacy variants like
 * "Restaurant" too.
 *
 * Every theme defines:
 *   backgroundBase       — CSS background-image / gradient stack for
 *                          the page hero surface (behind the wheel).
 *   ambientColor         — soft radial spotlight tint behind the wheel.
 *   slicePrimary /
 *   sliceSecondary       — alternating wheel-slice fills used when
 *                          SafeColors doesn't derive a vendor palette.
 *   iconColor            — mono icon tint for category cues.
 *   environmentalAsset   — inline SVG decoration rendered as a subtle
 *                          overlay (line-art only, never photos).
 *   texture              — CSS pattern overlay (noise / dots / grid).
 *   lightStyle           — "spotlight" | "sidelight" | "flat".
 *   decorativeElements   — labels used by RewardWheel for tiny accents.
 *
 * CityDooh gold (#F5A800) is applied on top of every theme by
 * RewardWheel and PlayPage — it stays the constant platform signature.
 */

/* ─── Reusable SVG line-art snippets (data URLs) ────────────────── */

/** Very subtle repeating pattern helpers — all inline data URLs so
 *  they cost 0 network requests. */
const NOISE_DOT = (color = "#ffffff", opacity = 0.06) =>
  `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22'><circle cx='1' cy='1' r='1' fill='${encodeURIComponent(color)}' opacity='${opacity}'/></svg>")`;

const CIRCUIT_LINES = (color = "#3b82f6", opacity = 0.12) =>
  `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'><g stroke='${encodeURIComponent(color)}' stroke-opacity='${opacity}' fill='none' stroke-width='1'><path d='M0 30 L40 30 L40 60 L80 60 L80 100 L120 100'/><path d='M0 90 L20 90 L20 50 L60 50'/><circle cx='40' cy='30' r='3'/><circle cx='80' cy='60' r='3'/><circle cx='20' cy='90' r='3'/></g></svg>")`;

const GRID_LINES = (color = "#ffffff", opacity = 0.05) =>
  `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><path d='M40 0 L0 0 0 40' fill='none' stroke='${encodeURIComponent(color)}' stroke-opacity='${opacity}' stroke-width='1'/></svg>")`;

const HALFTONE = (color = "#ffffff", opacity = 0.08) =>
  `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16'><circle cx='8' cy='8' r='2' fill='${encodeURIComponent(color)}' opacity='${opacity}'/></svg>")`;

/* ─── Themes (17 keys mapped to actual business_type values) ────── */

// eslint-disable-next-line no-unused-vars
const _unused = ""; // keep-alive

export const CATEGORY_THEMES = {
  cafe: {
    label: "Cafe",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #4a2c1a 0%, #2b1710 60%, #17100a 100%)",
    ambientColor: "rgba(217, 149, 71, 0.22)",
    slicePrimary: "#c98a5b",
    sliceSecondary: "#f0d3a8",
    iconColor: "#f0d3a8",
    texture: HALFTONE("#f0d3a8", 0.05),
    lightStyle: "spotlight",
  },
  bakery_sweets: {
    label: "Bakery & Sweets",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #5a3820 0%, #33200f 60%, #1c1108 100%)",
    ambientColor: "rgba(238, 178, 100, 0.28)",
    slicePrimary: "#d4915a",
    sliceSecondary: "#f4dab3",
    iconColor: "#f4dab3",
    texture: NOISE_DOT("#f4dab3", 0.05),
    lightStyle: "spotlight",
  },
  retail_store: {
    label: "Retail Store",
    backgroundBase: "radial-gradient(110% 85% at 50% 0%, #0a3020 0%, #051810 60%, #030c08 100%)",
    ambientColor: "rgba(72, 214, 148, 0.20)",
    slicePrimary: "#3fb37a",
    sliceSecondary: "#c8e6d4",
    iconColor: "#c8e6d4",
    texture: GRID_LINES("#c8e6d4", 0.04),
    lightStyle: "spotlight",
  },
  supermarket_grocery: {
    label: "Supermarket & Grocery",
    backgroundBase: "radial-gradient(110% 85% at 50% 0%, #0d3d24 0%, #06210f 60%, #030f07 100%)",
    ambientColor: "rgba(120, 214, 130, 0.22)",
    slicePrimary: "#5ec27a",
    sliceSecondary: "#e2f0d4",
    iconColor: "#e2f0d4",
    texture: GRID_LINES("#a0d090", 0.05),
    lightStyle: "flat",
  },
  clothing_fashion: {
    label: "Clothing & Fashion",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #4a1024 0%, #260716 60%, #100309 100%)",
    ambientColor: "rgba(240, 156, 173, 0.20)",
    slicePrimary: "#d95a7d",
    sliceSecondary: "#f4c8d4",
    iconColor: "#f4c8d4",
    texture: NOISE_DOT("#f4c8d4", 0.05),
    lightStyle: "spotlight",
  },
  footwear: {
    label: "Footwear",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #3a1220 0%, #200812 60%, #0f0407 100%)",
    ambientColor: "rgba(228, 148, 148, 0.20)",
    slicePrimary: "#c26074",
    sliceSecondary: "#f0d0d4",
    iconColor: "#f0d0d4",
    texture: HALFTONE("#f0d0d4", 0.06),
    lightStyle: "sidelight",
  },
  jewellery: {
    label: "Jewellery",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #3a1b47 0%, #1e0d29 60%, #0d0614 100%)",
    ambientColor: "rgba(255, 195, 88, 0.28)",
    slicePrimary: "#a06bcf",
    sliceSecondary: "#e4c88b",
    iconColor: "#e4c88b",
    texture: NOISE_DOT("#f5d78c", 0.06),
    lightStyle: "spotlight",
  },
  electronics_mobiles: {
    label: "Electronics & Mobiles",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #051f4a 0%, #030f28 60%, #01060f 100%)",
    ambientColor: "rgba(58, 148, 255, 0.24)",
    slicePrimary: "#3a94ff",
    sliceSecondary: "#c8dfff",
    iconColor: "#c8dfff",
    texture: CIRCUIT_LINES("#5aa8ff", 0.10),
    lightStyle: "sidelight",
  },
  salon_spa: {
    label: "Salon & Spa",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #3d2418 0%, #21140c 60%, #100a05 100%)",
    ambientColor: "rgba(240, 190, 176, 0.20)",
    slicePrimary: "#c8907b",
    sliceSecondary: "#f4d8ce",
    iconColor: "#f4d8ce",
    texture: HALFTONE("#f4d8ce", 0.05),
    lightStyle: "spotlight",
  },
  pharmacy_healthcare: {
    label: "Pharmacy & Healthcare",
    backgroundBase: "radial-gradient(110% 85% at 50% 0%, #0a3542 0%, #051d24 60%, #030f13 100%)",
    ambientColor: "rgba(90, 224, 220, 0.22)",
    slicePrimary: "#3ec4c0",
    sliceSecondary: "#cceeeb",
    iconColor: "#cceeeb",
    texture: GRID_LINES("#cceeeb", 0.05),
    lightStyle: "flat",
  },
  optical_eyewear: {
    label: "Optical & Eyewear",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #3a1e10 0%, #201009 60%, #100804 100%)",
    ambientColor: "rgba(238, 178, 148, 0.22)",
    slicePrimary: "#c98060",
    sliceSecondary: "#f2d4c3",
    iconColor: "#f2d4c3",
    texture: NOISE_DOT("#f2d4c3", 0.05),
    lightStyle: "sidelight",
  },
  furniture_home_decor: {
    label: "Furniture & Home Decor",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #241a0c 0%, #140f06 60%, #0a0803 100%)",
    ambientColor: "rgba(228, 178, 88, 0.25)",
    slicePrimary: "#c99552",
    sliceSecondary: "#f0d8a8",
    iconColor: "#f0d8a8",
    texture: HALFTONE("#f0d8a8", 0.05),
    lightStyle: "sidelight",
  },
  automobile: {
    label: "Automobile (Showroom/Service)",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #241608 0%, #140c04 60%, #0a0602 100%)",
    ambientColor: "rgba(255, 168, 68, 0.22)",
    slicePrimary: "#e69440",
    sliceSecondary: "#f4d3a8",
    iconColor: "#f4d3a8",
    texture: GRID_LINES("#e69440", 0.06),
    lightStyle: "sidelight",
  },
  hotel_hospitality: {
    label: "Hotel & Hospitality",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #40121c 0%, #22080f 60%, #100307 100%)",
    ambientColor: "rgba(238, 178, 108, 0.24)",
    slicePrimary: "#c26074",
    sliceSecondary: "#e4c88b",
    iconColor: "#e4c88b",
    texture: NOISE_DOT("#e4c88b", 0.05),
    lightStyle: "spotlight",
  },
  gym_fitness: {
    label: "Gym & Fitness",
    backgroundBase: "radial-gradient(110% 85% at 50% 0%, #101010 0%, #060606 60%, #020202 100%)",
    ambientColor: "rgba(180, 240, 88, 0.22)",
    slicePrimary: "#a6e047",
    sliceSecondary: "#e2f2c4",
    iconColor: "#e2f2c4",
    texture: GRID_LINES("#a6e047", 0.06),
    lightStyle: "sidelight",
  },
  education_coaching: {
    label: "Education & Coaching",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #0a1e42 0%, #061028 60%, #02060f 100%)",
    ambientColor: "rgba(120, 168, 255, 0.24)",
    slicePrimary: "#5a8cff",
    sliceSecondary: "#c8dbff",
    iconColor: "#c8dbff",
    texture: GRID_LINES("#c8dbff", 0.05),
    lightStyle: "flat",
  },
  other: {
    label: "Other",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #1e1e1e 0%, #0d0d0d 60%, #050505 100%)",
    ambientColor: "rgba(245, 168, 0, 0.22)",  // CityDooh gold fallback
    slicePrimary: "#F5A800",
    sliceSecondary: "#0A0A0A",
    iconColor: "#F5A800",
    texture: HALFTONE("#F5A800", 0.05),
    lightStyle: "spotlight",
  },
  // 02/08 — Events: deep purple base, gold accent (keeps CityDooh gold
  // as the consistent platform accent across every category rather than
  // introducing a second unrelated accent color).
  events: {
    label: "Events",
    backgroundBase: "radial-gradient(120% 90% at 50% 0%, #5B21B6 0%, #2E1065 60%, #170535 100%)",
    ambientColor: "rgba(245, 168, 0, 0.28)",
    slicePrimary: "#8B5CF6",
    sliceSecondary: "#F5A800",
    iconColor: "#F5A800",
    texture: NOISE_DOT("#F5A800", 0.06),
    lightStyle: "spotlight",
  },
};

/** Intensity multipliers — used by PlayPage to dial the theme up/down. */
export const INTENSITY = {
  minimal:   { ambient: 0.4, texture: 0.4, decorations: 0.2 },
  branded:   { ambient: 1.0, texture: 1.0, decorations: 1.0 },  // DEFAULT
  immersive: { ambient: 1.3, texture: 1.3, decorations: 1.4 },
};

/** Manual theme variants — code-only, no AI. Vendors pick one of these
 *  swatch variants to further personalize within a category. Each
 *  variant is just a lightweight override of ambient + slice hues.
 *
 *  IMPORTANT — naming: variant keys must NOT collide with legacy
 *  `campaign.theme` values (citydooh, christmas, diwali, royal, onyx,
 *  sky, sunset, gold, midnight, emerald, coral, crimson, blossom,
 *  valentine, birthday, sale, custom). Previously "onyx" and "royal"
 *  collided with the legacy themes of the same name — we renamed
 *  them to "graphite" and "sapphire". Old campaigns saved with the
 *  original keys still resolve because we keep the legacy keys as
 *  aliases pointing at the identical override values. */
export const MANUAL_VARIANTS = {
  aurora:   { ambient: "rgba(120,180,255,0.25)", slicePrimary: "#5a8cff", sliceSecondary: "#a0c8ff" },
  ember:    { ambient: "rgba(255,120,68,0.25)",  slicePrimary: "#e26a45", sliceSecondary: "#f2b090" },
  graphite: { ambient: "rgba(200,200,200,0.15)", slicePrimary: "#2a2a2a", sliceSecondary: "#7a7a7a" },
  sapphire: { ambient: "rgba(180,120,220,0.25)", slicePrimary: "#8a5ac8", sliceSecondary: "#c8a8e6" },
  sunrise:  { ambient: "rgba(255,200,88,0.28)",  slicePrimary: "#f4b840", sliceSecondary: "#fbdd9c" },
  // ── Backward-compat aliases (do NOT surface in wizard UI) ─────────
  onyx:     { ambient: "rgba(200,200,200,0.15)", slicePrimary: "#2a2a2a", sliceSecondary: "#7a7a7a" },
  royal:    { ambient: "rgba(180,120,220,0.25)", slicePrimary: "#8a5ac8", sliceSecondary: "#c8a8e6" },
};

/* ─── Public API ────────────────────────────────────────────────── */

/** Convert a `business_type` string to a theme key.
 *  - "Cafe" → "cafe"
 *  - "Bakery & Sweets" → "bakery_sweets"
 *  - "Automobile (Showroom/Service)" → "automobile"
 *  - "Restaurant" (legacy) → "cafe"
 *  - null / unknown → "other" */
export function slugifyBusinessType(bt) {
  if (!bt) return "other";
  const s = String(bt).toLowerCase().trim();
  // Legacy aliases
  if (s === "restaurant") return "cafe";
  if (s === "retail") return "retail_store";
  // Strip parenthetical suffix (e.g. "Automobile (Showroom/Service)")
  const noParens = s.replace(/\s*\([^)]*\)\s*/g, "").trim();
  // Compress non-alnum runs to underscores
  const slug = noParens.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const known = {
    cafe: "cafe",
    bakery_sweets: "bakery_sweets",
    retail_store: "retail_store",
    supermarket_grocery: "supermarket_grocery",
    clothing_fashion: "clothing_fashion",
    footwear: "footwear",
    jewellery: "jewellery",
    electronics_mobiles: "electronics_mobiles",
    salon_spa: "salon_spa",
    pharmacy_healthcare: "pharmacy_healthcare",
    optical_eyewear: "optical_eyewear",
    furniture_home_decor: "furniture_home_decor",
    automobile: "automobile",
    hotel_hospitality: "hotel_hospitality",
    gym_fitness: "gym_fitness",
    education_coaching: "education_coaching",
    events: "events",
    other: "other",
  };
  return known[slug] || "other";
}

/** Resolve a theme by its slug, ALWAYS returning a valid theme
 *  (falls back to "other" for unknown or missing keys). */
export function getCategoryTheme(key) {
  return CATEGORY_THEMES[key] || CATEGORY_THEMES.other;
}

/** Compose the final theme object PlayPage renders from:
 *   categorySlug     — from campaign.theme / vendor.business_type
 *   intensity        — "minimal" | "branded" | "immersive" (default "branded")
 *   manualVariant    — optional swatch override from MANUAL_VARIANTS
 *   safePalette      — output of SafeColors.normalizePalette
 */
export function composeTheme({ categorySlug, intensity = "branded", manualVariant, safePalette }) {
  const cat = getCategoryTheme(categorySlug);
  const mv = manualVariant && MANUAL_VARIANTS[manualVariant];
  const intMul = INTENSITY[intensity] || INTENSITY.branded;

  return {
    key: categorySlug,
    label: cat.label,
    backgroundBase: cat.backgroundBase,
    ambientColor: mv?.ambient || cat.ambientColor,
    ambientOpacity: intMul.ambient,
    textureImage: cat.texture,
    textureOpacity: intMul.texture,
    lightStyle: cat.lightStyle,
    // Slice fills — vendor safePalette wins, else manual variant, else category
    sliceA: safePalette?.sliceA || mv?.slicePrimary || cat.slicePrimary,
    sliceB: safePalette?.sliceB || mv?.sliceSecondary || cat.sliceSecondary,
    onSlice: safePalette?.onSlice || "#0A0A0A",
    iconColor: cat.iconColor,
    citydoohGold: "#F5A800",   // NEVER overridden
    ink: "#0A0A0A",
    intensity,
  };
}

/** List of themes for admin UI pickers. */
export const CATEGORY_THEME_LIST = Object.entries(CATEGORY_THEMES).map(([key, t]) => ({
  key, label: t.label,
}));
